import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, AlertCircle, Snowflake } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuth";

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (user) nav("/admin/orders", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      nav("/admin/orders", { replace: true });
    } catch (err) {
      const code = err?.response?.status;
      const msg =
        code === 429
          ? "Too many failed attempts. Try again in 15 minutes."
          : err?.response?.data?.detail || "Sign-in failed.";
      setError(typeof msg === "string" ? msg : "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ice-light flex items-center justify-center px-4 py-10" data-testid="page-admin-login">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-7">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-navy text-cyan">
            <Snowflake size={22} strokeWidth={2.2} />
          </span>
          <div className="mt-4 text-center">
            <div className="text-xl font-bold text-navy tracking-tight">PeptiChill</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted">Admin</div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-2xl bg-white border border-silver/40 shadow-card p-6 sm:p-8"
          data-testid="admin-login-form"
          noValidate
        >
          <h2 className="text-base font-bold text-navy mb-5">Sign in</h2>

          <label className="block text-xs uppercase tracking-widest text-muted mb-2">
            Username
          </label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            required
            data-testid="admin-login-username"
            className="w-full h-11 rounded-xl border border-silver/70 bg-white px-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition"
          />

          <label className="block text-xs uppercase tracking-widest text-muted mt-4 mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="admin-login-password"
            className="w-full h-11 rounded-xl border border-silver/70 bg-white px-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition"
          />

          {error && (
            <div className="mt-4 flex items-start gap-2 text-sm text-destructive rounded-xl bg-destructive/5 border border-destructive/20 px-3 py-2.5"
                 data-testid="admin-login-error" role="alert">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white h-12 text-sm font-semibold hover:brightness-110 transition shadow-card disabled:opacity-70"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            <Lock size={14} /> {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
